import React from "react";
import clsx from "clsx";

import styles from "./index.module.scss";

/** The two widths a letter is checked at. */
export type Device = "desktop" | "mobile";

interface LetterSheetProps extends React.PropsWithChildren {
  device: Device;
  /** The letter's base ground, from its own styles. */
  background: string;
  /** What the letter stands on: the ground an email client paints behind the
   * content column, and what shows beside and below the sheet. */
  pageBackground: string;
  /** The face the whole letter is set in, from its own styles. */
  fontFamily: string;
}

/**
 * The white the letter is printed on, and the viewport around it.
 *
 * Used by the builder's canvas and by the send screen's preview, so a letter
 * is the same width and sits on the same ground wherever it is shown. What
 * differs between the two is what goes inside — sections that can be dragged
 * and selected, or sections that can only be read.
 */
export default function LetterSheet({
  device,
  background,
  pageBackground,
  fontFamily,
  children,
}: LetterSheetProps) {
  const mobile = device === "mobile";

  return (
    <div
      className={clsx(styles.band, mobile && styles.bandMobile)}
      // Desktop only. On a phone the letter is the whole width there is, so
      // there is nothing beside it for a background to show through — and the
      // margin a client leaves outside it belongs to the client, not the
      // letter. Painting it would promise a band nobody will see.
      style={mobile ? undefined : { background: pageBackground }}
    >
      <div
        className={clsx(styles.sheet, device === "desktop" && styles.desktop)}
        // Set once here rather than on each section: everything inside the
        // sheet inherits it, which is also how an email carries a typeface.
        style={{ background, fontFamily }}
      >
        {children}
      </div>
    </div>
  );
}
